<template>
  <v-dialog v-model="visible" max-width="440" persistent>
    <!-- Opt-in era (2026-09-11): the viewer has NO encryption identity at all
         (e.g. a report recipient who accepted the invite before setting up
         encryption). Nothing to unlock — send them to set it up. -->
    <v-card v-if="mode === 'setup'">
      <v-card-title class="text-h6 d-flex align-center">Set up End-to-End Encryption <encryption-gen-badge generation="new" /></v-card-title>
      <v-card-text>
        <p class="mb-3">
          The information you're trying to view uses our new End-to-End Encryption format. In order to view this account's screenshots and events,
          you must enable the new encryption format on your account. You can easily set it up on your <strong>Account</strong> page, it only takes
          about a minute.
        </p>
        <p class="mb-0">
          Once you have completed the setup, the owner of this data needs to approve your encrypted access. Tell them to <strong>Grant Encrypted
          Access</strong> on the <strong>Email Recipients</strong> page from their online account.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="!!busy" @click="close">Not now</v-btn>
        <v-btn color="primary" class="text-white" :loading="busy === 'nav'" @click="goToSetup">Set up encryption</v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-else>
      <v-card-title class="text-h6 d-flex align-center">Unlock encrypted data <encryption-gen-badge generation="new" /></v-card-title>
      <v-card-text>
        <p class="mb-4">
          This browser doesn't hold the encryption key for
          <strong>{{ accountId }}</strong> yet. Unlock it one of these ways:
        </p>

        <v-btn
          v-if="passkeyAvailable"
          color="primary"
          block
          class="mb-3 text-white"
          :loading="busy === 'passkey'"
          :disabled="!!busy"
          @click="unlockWithPasskey"
        >
          Unlock with passkey
        </v-btn>

        <v-btn
          variant="outlined"
          block
          class="mb-4"
          :disabled="!!busy"
          @click="goToLinkDevice"
        >
          Approve from another device
        </v-btn>

        <v-divider class="mb-3" />
        <p class="text-caption mb-2">Or use your saved recovery code file:</p>
        <recovery-code-file-button
          :loading="busy === 'recovery' && !showManualRecovery"
          :disabled="!!busy"
          @code="onRecoveryCodeLoaded"
          @error="error = $event"
        />
        <div v-if="!showManualRecovery" class="text-center mt-1">
          <v-btn
            variant="text"
            size="small"
            :disabled="!!busy"
            @click="showManualRecovery = true"
          >
            Type the code instead
          </v-btn>
        </div>
        <template v-else>
          <v-text-field
            :model-value="recoveryCode"
            class="mt-3"
            density="compact"
            variant="outlined"
            placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
            autocomplete="off"
            autofocus
            :disabled="!!busy"
            @update:model-value="onRecoveryCodeInput"
            @keyup.enter="submitRecovery"
          />
          <v-btn
            variant="outlined"
            block
            :loading="busy === 'recovery'"
            :disabled="!!busy || !recoveryCode"
            @click="submitRecovery"
          >
            Unlock with typed code
          </v-btn>
        </template>

        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-3">
          {{ error }}
        </v-alert>

        <!-- Finding ⑯ / OWNER-RESET.md: the way out for an account that lost
             every unlock method lives on the Account page — a viewer-only
             account simply starts over; an account with monitored devices
             starts over AND loses its encrypted history. -->
        <p class="text-caption text-medium-emphasis mt-4 mb-0">
          Lost every way to unlock? If this is your own account, you can reset
          its encryption from your
          <a href="#" @click.prevent="goToAccount">Account page</a>
          (encrypted screenshots and events recorded so far are deleted;
          monitored devices pause briefly and rejoin on their own).
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="!!busy" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  // NC1 (chunk 4) viewer-unlock dialog — the libsodium-scheme sibling of
  // E2EERecoveryPasswordRequest.vue (which stays Virgil-only). Opened via
  // Nc1UnlockPrompt.request(accountId); successful unlocks broadcast on the
  // shared E2EEPasscodePrompt.onUnlock channel so failed tiles retry.
  import { Nc1UnlockPrompt, passkeyUnlock, recoveryUnlock, passkeyUnlockAvailable, accountHasIdentity } from '@/services/e2ee/nc1/Unlock'
  import { maskRecoveryCodeField } from '@/services/e2ee/nc1/RecoveryCodeInput'
  import { dialogSafeNavigate } from '@/lib/dialogSafeNavigate'
  import RecoveryCodeFileButton from '@/components/e2ee/RecoveryCodeFileButton.vue'

  export default {
    name: 'Nc1UnlockDialog',
    components: { RecoveryCodeFileButton },
    data () {
      return {
        visible: false,
        mode: 'unlock',    // 'unlock' | 'setup' (no identity to unlock — opt-in era)
        accountId: null,
        passkeyAvailable: false,
        recoveryCode: '',
        showManualRecovery: false,
        busy: null,
        error: null,
      }
    },
    beforeMount () {
      Nc1UnlockPrompt.onRequest = (accountId) => this.open(accountId)
    },
    beforeUnmount () {
      Nc1UnlockPrompt.onRequest = null
    },
    methods: {
      async open (accountId) {
        this.accountId = accountId
        this.error = null
        this.recoveryCode = ''
        this.showManualRecovery = false
        this.passkeyAvailable = false
        // Encryption is opt-in (2026-09-11): the account may simply have no
        // identity yet, in which case every unlock option is a dead end —
        // show the set-up card instead. Decide the card BEFORE showing the
        // dialog: opening it first flashed the unlock card for the probe's
        // round trip (user-reported 2026-09-11). An unanswered probe (null)
        // keeps the unlock options rather than blocking a working browser.
        const hasIdentity = await accountHasIdentity(accountId)
        this.mode = hasIdentity === false ? 'setup' : 'unlock'
        this.visible = true
        if (this.mode === 'unlock') {
          this.passkeyAvailable = await passkeyUnlockAvailable(accountId)
        }
      },
      async unlockWithPasskey () {
        this.busy = 'passkey'
        this.error = null
        try {
          await passkeyUnlock(this.accountId)
          this.finish()
        } catch (e) {
          this.error = e.message || 'Passkey unlock failed.'
        } finally {
          this.busy = null
        }
      },
      onRecoveryCodeInput (value) {
        this.recoveryCode = maskRecoveryCodeField(value)
      },
      // A code found in the file is already validated — unlock straight away.
      onRecoveryCodeLoaded (code) {
        this.recoveryCode = code
        this.submitRecovery()
      },
      async submitRecovery () {
        this.busy = 'recovery'
        this.error = null
        try {
          await recoveryUnlock(this.accountId, this.recoveryCode)
          this.finish()
        } catch (e) {
          this.error = e.message || 'Recovery unlock failed.'
        } finally {
          this.busy = null
        }
      },
      // Every navigation out of this dialog goes through dialogSafeNavigate:
      // the dashboard dialog underneath (screenshot viewer, gallery, ...)
      // registers a guard that swallows the first push as "close me", which
      // made the Account-page link look like it went to the dashboard
      // (2026-09-11).
      async navigateTo (to) {
        this.busy = 'nav'
        try {
          this.close()
          const ok = await dialogSafeNavigate(this.$router, to)
          if (!ok) console.log('[nc1] navigation cancelled by an open dialog: ' + JSON.stringify(to))
        } finally {
          this.busy = null
        }
      },
      accountPath () {
        // Same in-app WebView workaround as HomeV2.openAccountPageForEnablingE2EE.
        return this.$store.state.inApp ? '/inAppAccountHack' : '/account'
      },
      goToLinkDevice () {
        this.navigateTo('/link-device')
      },
      goToAccount () {
        this.navigateTo(this.accountPath())
      },
      // ?forceEnableE2ee=nc1 auto-starts the NC1 opt-in on the Account page —
      // the scheme the account being viewed uses, so no chooser here.
      goToSetup () {
        this.navigateTo({ path: this.accountPath(), query: { forceEnableE2ee: 'nc1' } })
      },
      finish () {
        this.visible = false
        Nc1UnlockPrompt.resolve(this.accountId)
      },
      close () {
        this.visible = false
        // Explicit close without unlocking — let deferred dialogs resurface.
        Nc1UnlockPrompt.dismissed(this.accountId)
      },
    },
  }
</script>
